/* AIWritingAssist (iter104) — "Help me write" panel under a textarea.

   Two modes: draft from a few gentle prompts, or polish the writer's
   own words. Tone is selectable. Streams the result token-by-token,
   then the writer can insert it (always editable afterwards). */
import React, { useRef, useState } from 'react';
import { Sparkles, X, Wand2, PenLine, RefreshCw, Check, Loader2 } from 'lucide-react';
import { API } from '../lib/api';
import { toast } from 'sonner';

const TONES = [
  { id: 'warm',        label: 'Warm' },
  { id: 'formal',      label: 'Formal' },
  { id: 'celebratory', label: 'Celebratory' },
  { id: 'faith',       label: 'Faith-friendly' },
];

export function AIWritingAssist({
  context = 'memory',          // 'life_summary' | 'memory'
  deceasedName = '',
  relationship = '',
  getCurrentText,              // () => string in the target textarea
  onInsert,                    // (text) => void
  testIdPrefix = 'ai-assist',
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('draft');
  const [tone, setTone] = useState('warm');
  const [details, setDetails] = useState('');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);

  const generate = async () => {
    const current = (getCurrentText?.() || '').trim();
    if (mode === 'polish' && current.length < 15) {
      toast.error('Write a few words in the box above first — then I can polish them.');
      return;
    }
    if (mode === 'draft' && details.trim().length < 10) {
      toast.error('Share a detail or two so the words feel like them.');
      return;
    }
    setBusy(true);
    setPreview('');
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/ai/writing-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ctrl.signal,
        body: JSON.stringify({
          mode, tone, context,
          deceased_name: deceasedName || null,
          relationship: relationship || null,
          details: mode === 'draft' ? details : null,
          text: mode === 'polish' ? current : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || 'The assistant is unavailable right now.');
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (payload === '[DONE]') continue;
          try {
            const j = JSON.parse(payload);
            if (j.error) throw new Error(j.error);
            if (j.t) { acc += j.t; setPreview(acc); }
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            throw e;
          }
        }
      }
      if (!acc.trim()) throw new Error('No text came back — please try again.');
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(e.message || 'Something went wrong.');
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const insert = () => {
    onInsert?.(preview.trim());
    setOpen(false);
    setPreview('');
    toast.success('Added — feel free to edit it to make it yours.');
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--accent))] hover:text-[hsl(var(--primary))] transition-colors"
        data-testid={`${testIdPrefix}-open`}
      >
        <Sparkles size={14} /> Help me find the words
      </button>
    );
  }

  return (
    <div
      className="mt-3 rounded-xl border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--accent)/0.05)] p-4"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--accent))] flex items-center gap-1.5">
          <Sparkles size={13} /> Writing assistant
        </div>
        <button
          type="button"
          onClick={() => { abortRef.current?.abort(); setOpen(false); }}
          className="text-[hsl(var(--primary)/0.5)] hover:text-[hsl(var(--primary))]"
          data-testid={`${testIdPrefix}-close`}
          aria-label="Close writing assistant"
        >
          <X size={16} />
        </button>
      </div>

      {/* Mode tabs */}
      <div className="mt-3 flex gap-2">
        {[
          { id: 'draft',  label: 'Write it for me', Icon: Wand2 },
          { id: 'polish', label: 'Polish my words', Icon: PenLine },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold transition-colors ${
              mode === id
                ? 'border-[hsl(var(--accent))] bg-white text-[hsl(var(--primary))]'
                : 'border-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary)/0.6)] hover:border-[hsl(var(--primary)/0.3)]'
            }`}
            data-testid={`${testIdPrefix}-mode-${id}`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* Tone pills */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {TONES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTone(t.id)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              tone === t.id
                ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
                : 'bg-transparent text-[hsl(var(--primary)/0.65)] border-[hsl(var(--primary)/0.2)] hover:border-[hsl(var(--primary)/0.45)]'
            }`}
            data-testid={`${testIdPrefix}-tone-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mode === 'draft' ? (
        <div className="mt-3">
          <label className="block text-[11px] font-semibold text-[hsl(var(--primary)/0.65)] mb-1.5">
            {context === 'life_summary'
              ? 'Tell us a little about them — what they loved, what they were known for, a phrase they always said…'
              : 'Share a moment or detail — a memory, something they did for you, what you\u2019ll miss…'}
          </label>
          <textarea
            rows={3}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={2000}
            className="ml-input !bg-white text-sm leading-relaxed"
            placeholder="e.g. She grew the best tomatoes on the street and never let anyone leave hungry…"
            data-testid={`${testIdPrefix}-details`}
          />
        </div>
      ) : (
        <p className="mt-3 text-xs text-[hsl(var(--primary)/0.6)] leading-relaxed">
          I&apos;ll gently improve the words you&apos;ve written in the box above — keeping your voice and your details.
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="ml-btn-gold !py-2 !px-4 text-xs disabled:opacity-60"
          data-testid={`${testIdPrefix}-generate`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {busy ? 'Writing…' : preview ? 'Try again' : mode === 'draft' ? 'Write a draft' : 'Polish it'}
        </button>
      </div>

      {preview && (
        <div className="mt-3">
          <div
            className="rounded-lg border border-[hsl(var(--primary)/0.15)] bg-white p-3.5 text-sm leading-relaxed text-[hsl(var(--primary))] whitespace-pre-wrap"
            data-testid={`${testIdPrefix}-preview`}
          >
            {preview}
          </div>
          {!busy && (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={insert}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
                data-testid={`${testIdPrefix}-use`}
              >
                <Check size={13} /> Use this text
              </button>
              <button
                type="button"
                onClick={generate}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[hsl(var(--primary)/0.25)] text-[hsl(var(--primary)/0.7)] text-xs font-semibold hover:border-[hsl(var(--primary))] transition-colors"
                data-testid={`${testIdPrefix}-retry`}
              >
                <RefreshCw size={12} /> Another version
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AIWritingAssist;
